import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import apiService from '../services/api';
import ChunkCard from '../components/ChunkCard';
import { useSession } from '../contexts/SessionContext';
import { useOffline } from '../contexts/OfflineContext';
import { useGuidedTour } from '../contexts/GuidedTourContext';
import { Chunk } from '../types';
import './SelectedChunksPage.css';
import '../components/ActionButtons.css';

const SelectedChunksPage = () => {
  const { currentSessionId, currentSession, getSelectedChunks, unmarkChunkSelected, clearSelectedChunks, markChunkDiscarded, userId, updateSession } = useSession();
  const { isActive: isTourActive, getCurrentStep } = useGuidedTour();
  const { isOnline } = useOffline();
  const [selectedChunks, setSelectedChunks] = useState<Chunk[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [scrollPositions, setScrollPositions] = useState<Record<string, number>>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const [userSources, setUserSources] = useState<string[]>(['diary']);
  const lastFetchedSessionRef = useRef<string | null>(null);
  const hasRestoredIndexRef = useRef(false);

  // Demo chunk for guided tour
  const demoChunk = useMemo<Chunk>(() => ({
    id: 'demo-selected-chunk-tour',
    score: 0.98,
    metadata: {
      text: 'This is a demo selected chunk! When you select chunks from the Search page, they appear here. You can review them, unselect them by clicking the Discard button or pressing ←, or delete them permanently using the D key. Once you\'re ready, head to the Export page to download your selected chunks.',
      source: 'tutorial',
      done: false,
    },
  }), []);

  const currentChunk = selectedChunks[currentIndex];

  // Load user sources on mount
  useEffect(() => {
    const loadSources = async () => {
      try {
        const response = await apiService.getUserSources(userId);
        setUserSources(response.data.sources);
      } catch (error) {
        console.error('Error loading sources:', error);
        // Keep default sources if loading fails
      }
    };
    loadSources();
  }, [userId]);

  // Load demo chunk when tour is active and on selected page
  useEffect(() => {
    if (isTourActive) {
      const currentStep = getCurrentStep();
      // Check if we're on a selected page step that should show chunks
      if (currentStep?.page === '/selected' &&
          (currentStep?.id === 'selected-chunks-list')) {
        setSelectedChunks([demoChunk]);
        setCurrentIndex(0);
        setLoading(false);
      }
    }
  }, [isTourActive, getCurrentStep, demoChunk]);

  // Fetch selected chunks when component mounts or session changes
  useEffect(() => {
    const fetchSelectedChunks = async () => {
      // Skip fetching if tour is active
      if (isTourActive) return;

      // Check if session changed - if so, reset the restored flag
      if (lastFetchedSessionRef.current !== currentSessionId) {
        hasRestoredIndexRef.current = false;
        lastFetchedSessionRef.current = currentSessionId;
      }

      // Get the saved index from session
      const savedIndex = currentSession?.lastSelectedIndex;

      // Skip re-fetch if:
      // 1. We've already restored for this session AND
      // 2. The saved index hasn't changed (i.e., we're not waiting for session to load with saved index)
      if (hasRestoredIndexRef.current && savedIndex !== undefined) {
        return;
      }

      setLoading(true);
      try {
        const chunks = await getSelectedChunks();
        setSelectedChunks(chunks);
        // Restore the saved index, ensuring it's within bounds
        const indexToRestore = savedIndex ?? 0;
        const validIndex = Math.min(indexToRestore, Math.max(0, chunks.length - 1));
        const finalIndex = validIndex >= 0 ? validIndex : 0;
        setCurrentIndex(finalIndex);
        setScrollPositions({}); // Clear scroll positions when fetching new chunks
        // Mark as restored only if we had a saved index to restore
        if (savedIndex !== undefined) {
          hasRestoredIndexRef.current = true;
        }
      } catch (error) {
        console.error('Error fetching selected chunks:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSelectedChunks();
  // currentSession?.lastSelectedIndex needed to restore index after session loads from backend
  // hasRestoredIndexRef prevents re-fetching when we update the index ourselves
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSessionId, getSelectedChunks, isTourActive, currentSession?.lastSelectedIndex]);

  // Persist currentIndex to session when it changes (for selected chunks)
  useEffect(() => {
    // Only persist if we have chunks and a valid session
    if (selectedChunks.length === 0 || !currentSessionId) return;

    // Skip persisting during tour
    if (isTourActive) return;

    updateSession(currentSessionId, {
      lastSelectedIndex: currentIndex
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  const handleSwipeLeft = useCallback(async () => {
    // Mark as discarded and remove from selected chunks
    if (currentChunk && currentSessionId) {
      // Optimistically update UI immediately
      const newChunks = selectedChunks.filter(c => c.id !== currentChunk.id);
      setSelectedChunks(newChunks);

      if (currentIndex >= newChunks.length && newChunks.length > 0) {
        setCurrentIndex(newChunks.length - 1);
      }

      // Trigger optimistic count update immediately (before any API calls)
      // This will decrement the count instantly
      const unmarkPromise = unmarkChunkSelected(currentChunk.id);

      try {
        // Mark as discarded in Pinecone for this session (parallel with unmark)
        await Promise.all([
          markChunkDiscarded(currentChunk.id),
          unmarkPromise
        ]);
      } catch (error) {
        console.error('Error handling swipe left:', error);
        // On error, restore the chunk to the list
        setSelectedChunks([...newChunks.slice(0, currentIndex), currentChunk, ...newChunks.slice(currentIndex)]);
      }
    }
  }, [currentChunk, currentSessionId, selectedChunks, currentIndex, unmarkChunkSelected, markChunkDiscarded]);

  const handleDeletePermanently = useCallback(async () => {
    if (!currentChunk) return;

    setShowDeleteConfirm(false);

    // Optimistically update UI immediately
    const newChunks = selectedChunks.filter(c => c.id !== currentChunk.id);
    setSelectedChunks(newChunks);

    if (currentIndex >= newChunks.length && newChunks.length > 0) {
      setCurrentIndex(newChunks.length - 1);
    }

    // Trigger optimistic count update immediately (before API call)
    const unmarkPromise = unmarkChunkSelected(currentChunk.id);

    try {
      await Promise.all([
        apiService.deleteRecord(currentChunk.id, userId),
        unmarkPromise
      ]);
    } catch (error) {
      console.error('Error deleting:', error);
      // On error, restore the chunk to the list
      setSelectedChunks([...newChunks.slice(0, currentIndex), currentChunk, ...newChunks.slice(currentIndex)]);
    }
  }, [currentChunk, selectedChunks, currentIndex, userId, unmarkChunkSelected]);

  const handleMarkDone = async () => {
    if (!currentChunk) return;

    // Optimistically update UI immediately
    const newChunks = selectedChunks.filter(c => c.id !== currentChunk.id);
    setSelectedChunks(newChunks);

    if (currentIndex >= newChunks.length && newChunks.length > 0) {
      setCurrentIndex(newChunks.length - 1);
    }

    // Trigger optimistic count update immediately (before API call)
    const unmarkPromise = unmarkChunkSelected(currentChunk.id);

    try {
      await Promise.all([
        apiService.markDone([currentChunk.id], userId),
        unmarkPromise
      ]);
    } catch (error) {
      console.error('Error marking done:', error);
      // On error, restore the chunk to the list
      setSelectedChunks([...newChunks.slice(0, currentIndex), currentChunk, ...newChunks.slice(currentIndex)]);
    }
  };

  const navigatePrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  }, [currentIndex]);

  const navigateNext = useCallback(() => {
    if (currentIndex < selectedChunks.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  }, [currentIndex, selectedChunks.length]);

  const handleChunkUpdate = (oldChunkId: string, updatedChunk: Chunk) => {
    // Optimistically update the chunk in the list
    setSelectedChunks(prevChunks =>
      prevChunks.map(chunk =>
        chunk.id === oldChunkId ? updatedChunk : chunk
      )
    );
  };

  const handleScrollPositionChange = (chunkId: string, scrollPosition: number) => {
    setScrollPositions(prev => ({
      ...prev,
      [chunkId]: scrollPosition
    }));
  };

  // Keyboard navigation for modal
  useEffect(() => {
    if (!showDeleteConfirm) return;

    const handleModalKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        // Toggle focus between delete and cancel buttons
        if (document.activeElement === deleteButtonRef.current) {
          cancelButtonRef.current?.focus();
        } else {
          deleteButtonRef.current?.focus();
        }
      }
    };

    window.addEventListener('keydown', handleModalKeyDown);

    return () => {
      window.removeEventListener('keydown', handleModalKeyDown);
    };
  }, [showDeleteConfirm]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore if modal is open
      if (showDeleteConfirm) return;

      // Ignore if any modal is open (check for modal-overlay in the DOM)
      if (document.querySelector('.modal-overlay')) return;

      // Ignore if user is typing in an input or textarea
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      switch (event.key) {
        case 'ArrowRight':
          // Select button is already selected (disabled), so this does nothing
          event.preventDefault();
          break;
        case 'ArrowLeft':
          // Discard for session
          event.preventDefault();
          handleSwipeLeft();
          break;
        case 'd':
        case 'D':
          // Show delete confirmation modal
          event.preventDefault();
          setShowDeleteConfirm(true);
          break;
        case 'ArrowUp':
          // Navigate to previous chunk
          event.preventDefault();
          navigatePrevious();
          break;
        case 'ArrowDown':
          // Navigate to next chunk
          event.preventDefault();
          navigateNext();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentChunk, currentIndex, selectedChunks, handleSwipeLeft, handleDeletePermanently, navigatePrevious, navigateNext, showDeleteConfirm]);

  const clearAllSelected = async () => {
    // Store current chunks for rollback on error
    const previousChunks = [...selectedChunks];
    const previousIndex = currentIndex;

    // Optimistically update UI immediately
    setSelectedChunks([]);
    setCurrentIndex(0);

    // Trigger optimistic count updates immediately (one decrement per chunk)
    // This will update the navigation count instantly
    const unmarkPromises = previousChunks.map(chunk => unmarkChunkSelected(chunk.id));

    try {
      // Wait for all unmark operations and the clear operation in parallel
      await Promise.all([clearSelectedChunks(), ...unmarkPromises]);
    } catch (error) {
      console.error('Error clearing selected chunks:', error);
      // On error, restore the previous state
      setSelectedChunks(previousChunks);
      setCurrentIndex(previousIndex);
    }
  };

  if (loading) {
    return (
      <div className="selected-page">
        {/* Offline status indicator */}
        {!isOnline && (
          <div className="offline-banner" style={{
            backgroundColor: '#d1ecf1',
            border: '1px solid #17a2b8',
            borderRadius: '4px',
            padding: '10px 12px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#0c5460',
            fontSize: '13px',
            fontWeight: 500
          }}>
            <span>📡</span> You are offline. Navigation works, but discard, delete, edit, clear selected, and mark done features require internet connection.
          </div>
        )}
        <div className="empty-state">
          <p>Loading selected chunks...</p>
        </div>
      </div>
    );
  }

  if (selectedChunks.length === 0) {
    return (
      <div className="selected-page">
        {/* Offline status indicator */}
        {!isOnline && (
          <div className="offline-banner" style={{
            backgroundColor: '#d1ecf1',
            border: '1px solid #17a2b8',
            borderRadius: '4px',
            padding: '10px 12px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#0c5460',
            fontSize: '13px',
            fontWeight: 500
          }}>
            <span>📡</span> You are offline. Navigation works, but discard, delete, edit, clear selected, and mark done features require internet connection.
          </div>
        )}
        <div className="empty-state">
          <p>No chunks selected yet.</p>
          <p>Go to the Search page and click the "Select" button to add chunks here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="selected-page">
      {/* Offline status indicator */}
      {!isOnline && (
        <div className="offline-banner" style={{
          backgroundColor: '#d1ecf1',
          border: '1px solid #17a2b8',
          borderRadius: '4px',
          padding: '10px 12px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#0c5460',
          fontSize: '13px',
          fontWeight: 500
        }}>
          <span>📡</span> You are offline. Navigation works, but discard, delete, edit, clear selected, and mark done features require internet connection.
        </div>
      )}

      <div className="selected-controls">
        <span className="total-count">Total Selected: {selectedChunks.length}</span>
        <button
          onClick={clearAllSelected}
          className="clear-all-button"
          aria-label="Clear all selected chunks"
          title={!isOnline ? "Clear selected requires internet connection" : ""}
          disabled={!isOnline}
        >
          Clear Selected
        </button>
      </div>

      <div className="chunk-display">
        <div className="chunk-header">
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="delete-button"
            title={!isOnline ? "Delete requires internet connection" : "Delete permanently"}
            aria-label="Delete permanently"
            disabled={!isOnline}
          >
            Delete permanently
          </button>
          <span className="chunk-counter">
            {currentIndex + 1} of {selectedChunks.length}
          </span>
          <button
            onClick={handleMarkDone}
            className="done-button"
            title={!isOnline ? "Mark as done requires internet connection" : "Mark as done"}
            aria-label="Mark as done for all sessions"
            disabled={!isOnline}
          >
            Mark as done for all sessions
          </button>
        </div>

        <ChunkCard
          chunk={currentChunk}
          onChunkUpdate={handleChunkUpdate}
          userId={userId}
          scrollPosition={scrollPositions[currentChunk?.id] || 0}
          onScrollPositionChange={handleScrollPositionChange}
          availableSources={userSources}
        />

        <div className="action-buttons">
          <button
            onClick={handleSwipeLeft}
            className="action-button discard-action"
            title={!isOnline ? "Discard requires internet connection" : "Discard for session"}
            aria-label="Discard for session"
            disabled={!isOnline}
          >
            ➖ Discard
          </button>
          <button
            onClick={navigatePrevious}
            disabled={currentIndex === 0}
            className="action-button nav-action"
            title="Previous chunk"
            aria-label="Previous chunk"
          >
            ↑ Prev
          </button>
          <button
            onClick={navigateNext}
            disabled={currentIndex === selectedChunks.length - 1}
            className="action-button nav-action"
            title="Next chunk"
            aria-label="Next chunk"
          >
            ↓ Next
          </button>
          <button
            disabled={true}
            className="action-button select-action"
            title="Already selected"
            aria-label="Already selected"
          >
            ➕ Select
          </button>
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Permanently</h3>
            <p>Are you sure you want to permanently delete this chunk?</p>
            <p>This action cannot be undone.</p>
            <div className="modal-buttons">
              <button
                ref={deleteButtonRef}
                onClick={handleDeletePermanently}
                className="delete-btn"
                autoFocus
              >
                Delete
              </button>
              <button
                ref={cancelButtonRef}
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectedChunksPage;
